import { useEffect, useState, useMemo, useRef } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { NavBar } from "@/components/NavBar";
import { ConfidenceLabel } from "@/components/ConfidenceLabel";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/hooks/useWebSocket";
import { T } from "@/lib/theme";

const C = T.dark;
const COUNTRY_CENTER = {
  "Ghana":         { lat: 7.95,  lng: -1.03, zoom: 7 },
  "Nigeria":       { lat: 9.08,  lng:  8.67, zoom: 6 },
  "Cote d'Ivoire": { lat: 7.54,  lng: -5.55, zoom: 7 },
  "Senegal":       { lat: 14.49, lng: -14.45, zoom: 7 },
};

export default function MapDashboard() {
  const { user } = useAuth();
  const [country, setCountry] = useState("Ghana");
  const [hour, setHour] = useState("live"); // "live" | 0..23
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ country });
      if (hour !== "live") params.set("hour", hour);
      const r = await api.get(`/map/hotspots?${params}`);
      setData(r.data);
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [country, hour]);
  // Real-time refresh: subscribe to the WS `pulse` topic. When pushes land,
  // re-query hotspots — but throttle to at most one fetch per 3s so a burst of
  // pulses doesn't hammer the backend or churn the React tree.
  const lastFetchRef = useRef(0);
  const pendingTimerRef = useRef(null);
  useWebSocket(hour === "live" ? ["pulse"] : [], (msg) => {
    if (msg.topic !== "pulse") return;
    const now = Date.now();
    const elapsed = now - lastFetchRef.current;
    if (elapsed >= 3000) {
      lastFetchRef.current = now;
      load();
    } else if (!pendingTimerRef.current) {
      // Schedule one trailing fetch when the throttle window closes
      pendingTimerRef.current = setTimeout(() => {
        pendingTimerRef.current = null;
        lastFetchRef.current = Date.now();
        load();
      }, 3000 - elapsed);
    }
  });
  useEffect(() => () => {
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
  }, []);

  const center = COUNTRY_CENTER[country] || COUNTRY_CENTER["Ghana"];
  const maxPeople = useMemo(
    () => Math.max(1, ...(data?.regions || []).map((r) => r.people || 0)),
    [data]
  );

  const radius = (people) => {
    if (!people) return 6;
    return 8 + Math.sqrt((people || 0) / maxPeople) * 32;
  };
  const color = (p) => p > maxPeople * 0.6 ? "#EF4444"
                     : p > maxPeople * 0.3 ? "#F59E0B"
                     : p > maxPeople * 0.1 ? "#00C896"
                     : "#3B82F6";

  return (
    <div data-testid="map-page" style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Inter',sans-serif", color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&family=Inter:wght@400;500;600;700&display=swap');
        .leaflet-container { background: ${C.bg} !important; }
        .leaflet-control-attribution { background: ${C.surface} !important; color: ${C.muted} !important; }
        .leaflet-control-attribution a { color: ${C.cyan} !important; }
      `}</style>
      <NavBar dark={true} />

      <div style={{ padding: "20px 32px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
          <div>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>LIVE AUDIENCE MAP</div>
            <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
              Where are people listening right now?
            </h1>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
              {data
                ? <>Tracking <b style={{ color: C.cyan }}>{(data.total_listeners || 0).toLocaleString()}</b> people across <b style={{ color: C.cyan }}>{data.regions?.length || 0}</b> regions in {country}.</>
                : "Loading…"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select data-testid="map-country" value={country} onChange={(e) => setCountry(e.target.value)}
              style={{ padding: "8px 14px", background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, fontFamily: "inherit", fontSize: 13 }}>
              {Object.keys(COUNTRY_CENTER).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="map-hour" value={hour} onChange={(e) => setHour(e.target.value === "live" ? "live" : Number(e.target.value))}
              style={{ padding: "8px 14px", background: C.surface, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, fontFamily: "inherit", fontSize: 13 }}>
              <option value="live">⏺ Live (now)</option>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00 (7-day avg)</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", gap: 16, alignItems: "start" }} className="map-grid">
          <div data-testid="map-canvas" style={{ height: 540, borderRadius: 14, overflow: "hidden", border: `1px solid ${C.border}` }}>
            <MapContainer center={[center.lat, center.lng]} zoom={center.zoom}
              key={country}
              style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://openstreetmap.org">OSM</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png" />
              {(data?.regions || []).filter((r) => r.lat && r.lng).map((r) => {
                const people = r.people || 0;
                return (
                <CircleMarker key={r.region_code}
                  center={[r.lat, r.lng]}
                  radius={radius(people)}
                  pathOptions={{ color: color(people), fillColor: color(people), fillOpacity: 0.55, weight: 2 }}>
                  <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                    <div style={{ fontFamily: "Inter,sans-serif" }}>
                      <b>{r.region_name}</b><br />
                      {people.toLocaleString()} listeners · {r.stations || 0} stations<br />
                      {r.penetration_pct || 0}% of pop
                    </div>
                  </Tooltip>
                  <Popup>
                    <div style={{ fontFamily: "Inter,sans-serif", minWidth: 180 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{r.region_name}</div>
                      <div style={{ fontSize: 12, color: "#6B7280" }}>
                        Listeners: <b>{people.toLocaleString()}</b><br />
                        Stations tracked: <b>{r.stations || 0}</b><br />
                        Penetration: <b>{r.penetration_pct || 0}%</b><br />
                        Population: {r.pop?.toLocaleString() || "—"}
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              );})}
            </MapContainer>
          </div>

          <div className="map-sidebar" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, maxHeight: 540, overflowY: "auto" }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Regional ranking</div>
            <div style={{ color: C.muted, fontSize: 11, marginBottom: 14 }}>
              {hour === "live" ? "Live listeners right now" : `${String(hour).padStart(2, "0")}:00 — 7-day average`}
            </div>
            {(data?.regions || []).map((r, i) => {
              const people = r.people || 0;
              return (
              <div key={r.region_code} data-testid={`map-region-${r.region_code}`}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", borderBottom: i < (data.regions.length - 1) ? `1px solid ${C.border}` : "none" }}>
                <div style={{ width: 22, color: C.muted, fontSize: 11, fontFamily: "'Space Grotesk',sans-serif" }}>{i + 1}</div>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: color(people), flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.region_name}
                  </div>
                  <div style={{ color: C.muted, fontSize: 10 }}>{r.stations || 0} stations · {r.penetration_pct || 0}%</div>
                </div>
                <div style={{ color: C.cyan, fontSize: 12, fontWeight: 700, fontFamily: "'Space Grotesk',sans-serif" }}>
                  {people > 999 ? `${(people / 1000).toFixed(1)}k` : people}
                </div>
              </div>
            );})}
          </div>
        </div>

        <div style={{ marginTop: 18, color: C.muted, fontSize: 11, display: "flex", flexWrap: "wrap", gap: 18, alignItems: "center" }}>
          <span>Legend:</span>
          {[["#3B82F6", "low"], ["#00C896", "medium"], ["#F59E0B", "high"], ["#EF4444", "hotspot"]].map(([col, label]) => (
            <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: "50%", background: col, display: "inline-block" }} /> {label}
            </span>
          ))}
          <ConfidenceLabel source="MODEL" size="xs" />
          <span>Live data refreshes every 60s. Historical hours are 7-day averages.</span>
        </div>
      </div>

      <style>{`
        @media (max-width: 920px) {
          .map-grid { grid-template-columns: 1fr !important; }
          .map-sidebar { max-height: 340px !important; }
        }
      `}</style>
    </div>
  );
}
